import { WarningCallout, WarningCalloutVariant } from '@hungryroot/pho';
import { InventoryStatus } from '@hungryroot/rest';
import { type AlgoliaInventoryInterface } from '@hungryroot/search';

interface Props {
  testid?: string;
  item: AlgoliaInventoryInterface;
  status: InventoryStatus;
  restrictions?: string[] | undefined;
}

const TagsToText: { [key: string]: string } = {
  has_meat: 'meat',
  has_fish: 'fish',
  has_shellfish: 'shellfish',
  has_dairy: 'dairy',
  has_egg: 'eggs',
  has_byproduct: 'honey',
  has_gluten: 'gluten',
  has_soy: 'soy',
  has_nut: 'nuts',
  has_peanut: 'peanuts',
};

function InventoryStatusValues(
  item: AlgoliaInventoryInterface,
  status:
    | InventoryStatus.Never
    | InventoryStatus.OutOfStock
    | InventoryStatus.Restrictions,
  restrictions?: string[]
) {
  if (status === InventoryStatus.Never) {
    if (
      item.index_ingredients !== undefined &&
      item.index_ingredients.length > 0
    ) {
      return {
        text: `Contains items you set to 'never'`,
        variant: WarningCalloutVariant.Never,
      };
    }
    return { text: `You set to 'never'`, variant: WarningCalloutVariant.Never };
  }

  if (status === InventoryStatus.Restrictions && restrictions) {
    const mappedRestrictions = restrictions
      .filter((r) => TagsToText[r] !== undefined)
      .map((r) => TagsToText[r]);
    const overflow =
      mappedRestrictions.length - 2 > 0 ? mappedRestrictions.length - 2 : 0;
    const text = `Contains ${mappedRestrictions.slice(0, 2).join(', ')}${
      overflow > 0 ? ` + ${overflow} more` : ''
    }`;

    return { text: text, variant: WarningCalloutVariant.Restrictions };
  }

  return { text: `Out of stock`, variant: WarningCalloutVariant.OutOfStock };
}

export function SearchInventoryStatus(props: Props) {
  const { testid, item, status, restrictions } = props;

  if (status === InventoryStatus.Ideal) {
    return null;
  }

  const { text, variant } = InventoryStatusValues(item, status, restrictions);

  return (
    <WarningCallout
      testid={`${testid}-inventory-status`}
      text={text}
      status={status}
      variant={variant}
    />
  );
}
